import type { ToolCallMessageContent } from "schemas/chat";
import type {
  ChatMessage as ChatMessageType,
  ChatToolSectionHeaderItem,
  GeneratePageToolResult,
  SuggestTemplatesToolResult,
} from "schemas/generated/chatMessage";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import LogoBadge from "@v2/components/LogoBadge";
import { ChatMessage } from "@v2/features/chat/components/chat-elements/chat-message/ChatMessage";
import IntegrationConnectCard from "@v2/features/chat/components/chat-elements/chat-message/IntegrationConnectCard";
import PageGenerationBlock from "@v2/features/chat/components/chat-elements/chat-message/PageGenerationBlock";
import SuggestedTemplates from "@v2/features/chat/components/chat-elements/chat-message/SuggestedTemplates";
import { ChatActions } from "@v2/features/chat/components/chat-elements/ChatActions";
import { ChatDebugPanel } from "@v2/features/chat/components/chat-elements/ChatDebugPanel";
import ToolSectionCard from "@v2/features/chat/components/chat-elements/message-group/ToolSectionCard";
import { useChatId } from "@v2/features/chat/hooks/useChatId";
import { findToolResult } from "@v2/features/chat/utils/findToolResult";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { motion } from "motion/react";
import {
  chatCodeChangesOverviewItemSchema,
  chatToolSectionHeaderItemSchema,
} from "schemas/chatMessage";

/**
 * Extracts and parses the toolSectionHeader message for dynamic header text
 */
const getHeaderText = (
  messages: ChatMessageType[],
): ChatToolSectionHeaderItem | undefined => {
  const toolSectionHeaderMessage = messages
    .find((msg) => {
      return msg.content.some((contentItem) => {
        return (
          contentItem.type === "text" &&
          contentItem.contentType === "toolSectionHeader"
        );
      });
    })
    ?.content.find((item) => {
      return item.type === "text" && item.contentType === "toolSectionHeader";
    });

  return toolSectionHeaderMessage &&
    toolSectionHeaderMessage.type === "text" &&
    toolSectionHeaderMessage.contentType === "toolSectionHeader" &&
    chatToolSectionHeaderItemSchema.safeParse(
      JSON.parse(toolSectionHeaderMessage.text),
    ).success
    ? chatToolSectionHeaderItemSchema.parse(
        JSON.parse(toolSectionHeaderMessage.text),
      )
    : undefined;
};

/**
 * Splits a message group into multiple sections for partitioned collapsible UI:
 *
 * - preToolMessages: Messages before any tool usage (including text before the first tool-call)
 * - toolSectionMessages: All messages after the first tool-call (excluding page generation and suggest templates messages)
 * - postToolMessages: Messages after all tool usage
 * - suggestTemplatesToolResult: Tool result for suggest templates
 * - suggestTemplatesToolResultMessageId: Message ID containing the suggest templates tool result
 * - pageGenerationToolResult: Tool result for page generation
 * - pageGenerationToolResultMessageId: Message ID containing the page generation tool result
 * - fileVersionId: any file version id from changes made in the agent loop
 */
const splitMessageGroup = (
  messages: ChatMessageType[],
): {
  preToolMessages: ChatMessageType[];
  toolSectionMessages: ChatMessageType[];
  postToolMessages: ChatMessageType[];
  suggestTemplatesToolResult?: SuggestTemplatesToolResult;
  suggestTemplatesToolResultMessageId?: string;
  pageGenerationToolResult?: GeneratePageToolResult;
  pageGenerationToolResultMessageId?: string;
  integrationConfigToolCall?: ToolCallMessageContent;
  integrationConfigIsConfigured?: boolean;
  fileVersionId?: string;
} => {
  const firstToolMessageIndex = messages.findIndex((msg) => {
    return msg.content.some((item) => item.type === "tool-call");
  });

  if (firstToolMessageIndex === -1) {
    return {
      preToolMessages: messages.filter((msg) => {
        return !msg.content.some((item) => {
          return (
            item.type === "text" && item.contentType === "codeChangesOverview"
          );
        });
      }),
      toolSectionMessages: [],
      postToolMessages: [],
      suggestTemplatesToolResult: undefined,
      suggestTemplatesToolResultMessageId: undefined,
      pageGenerationToolResult: undefined,
      pageGenerationToolResultMessageId: undefined,
      integrationConfigToolCall: undefined,
      integrationConfigIsConfigured: undefined,
      fileVersionId: undefined,
    };
  }

  const lastToolOrResultIndex = messages.findLastIndex((msg) => {
    return (
      msg.role === "tool" ||
      msg.content.some((item) => item.type === "tool-call")
    );
  });

  const preToolMessages = messages
    .slice(0, firstToolMessageIndex)
    .filter((msg) => msg.content.length > 0);

  // NOTE (Ben, 2025-11-10): We want to exclude page generation, suggest templates, and integration config messages from the tool section messages since we render them separately
  const toolSectionMessages = messages
    .slice(firstToolMessageIndex)
    .filter((msg) => {
      return !msg.content.some((item) => {
        return (
          (item.type === "tool-call" || item.type === "tool-result") &&
          (item.toolName === "generatePageTool" ||
            item.toolName === "suggestTemplatesTool" ||
            item.toolName === "getIntegrationConfigTool")
        );
      });
    });

  // NOTE (Ben, 2025-11-06): Handle case where first message contains both text and tool-call
  // Extract any text content that appears before the first tool-call in that message
  const firstToolMessage = messages[firstToolMessageIndex];
  if (firstToolMessage) {
    const firstNonExcludedToolCallIndex = firstToolMessage.content.findIndex(
      (item) => {
        return (
          (item.type === "tool-call" || item.type === "tool-result") &&
          item.toolName !== "generatePageTool" &&
          item.toolName !== "suggestTemplatesTool" &&
          item.toolName !== "getIntegrationConfigTool"
        );
      },
    );

    if (firstNonExcludedToolCallIndex === -1) {
      const firstAnyToolCallIndex = firstToolMessage.content.findIndex(
        (item) => {
          return item.type === "tool-call" || item.type === "tool-result";
        },
      );

      const preToolContent = firstToolMessage.content.slice(
        0,
        firstAnyToolCallIndex,
      );
      if (preToolContent.length > 0) {
        const preToolTextMessage: ChatMessageType = {
          ...firstToolMessage,
          content: preToolContent,
        };
        preToolMessages.push(preToolTextMessage);
      }
    } else {
      const preToolContent = firstToolMessage.content.slice(
        0,
        firstNonExcludedToolCallIndex,
      );

      if (preToolContent.length > 0) {
        const preToolTextMessage: ChatMessageType = {
          ...firstToolMessage,
          content: preToolContent,
        };
        preToolMessages.push(preToolTextMessage);
      }

      const toolAndAfterContent = firstToolMessage.content.slice(
        firstNonExcludedToolCallIndex,
      );

      toolSectionMessages[0] = {
        ...firstToolMessage,
        content: toolAndAfterContent.filter((item) => {
          return (
            (item.type === "tool-call" || item.type === "tool-result") &&
            item.toolName !== "generatePageTool" &&
            item.toolName !== "suggestTemplatesTool" &&
            item.toolName !== "getIntegrationConfigTool"
          );
        }),
      };
    }
  }

  const postToolMessages = messages
    .slice(lastToolOrResultIndex + 1)
    .filter((msg) => {
      return (
        msg.content.length > 0 &&
        !msg.content.some((item) => {
          return (
            item.type === "text" && item.contentType === "codeChangesOverview"
          );
        })
      );
    });

  // Find suggest templates tool call and its result
  const suggestTemplatesToolCall = messages
    .flatMap((msg) => msg.content)
    .find((item) => {
      return (
        item.type === "tool-call" && item.toolName === "suggestTemplatesTool"
      );
    });

  const {
    toolResult: suggestTemplatesToolResult,
    toolResultMessageId: suggestTemplatesToolResultMessageId,
  } =
    suggestTemplatesToolCall && suggestTemplatesToolCall.type === "tool-call"
      ? findToolResult(suggestTemplatesToolCall.toolCallId, messages)
      : { toolResult: null, toolResultMessageId: undefined };

  // Find page generation tool call and its result
  const pageGenerationToolCall = messages
    .flatMap((msg) => msg.content)
    .find((item) => {
      return item.type === "tool-call" && item.toolName === "generatePageTool";
    });

  const {
    toolResult: pageGenerationToolResult,
    toolResultMessageId: pageGenerationToolResultMessageId,
  } =
    pageGenerationToolCall && pageGenerationToolCall.type === "tool-call"
      ? findToolResult(pageGenerationToolCall.toolCallId, messages)
      : { toolResult: null, toolResultMessageId: undefined };

  const codeChangesOverviewMessage = messages
    .find((msg) => {
      return msg.content.some((contentItem) => {
        return (
          contentItem.type === "text" &&
          contentItem.contentType === "codeChangesOverview"
        );
      });
    })
    ?.content.find((item) => {
      return item.type === "text" && item.contentType === "codeChangesOverview";
    });

  const pageGenerationToolResultMessage = messages
    .find((msg) => {
      return msg.content.some((item) => {
        return (
          item.type === "tool-result" && item.toolName === "generatePageTool"
        );
      });
    })
    ?.content.find((item) => {
      return (
        item.type === "tool-result" && item.toolName === "generatePageTool"
      );
    });

  const { versionId: codeChangesOverviewVersionId } =
    (codeChangesOverviewMessage &&
      codeChangesOverviewMessage.type === "text" &&
      codeChangesOverviewMessage.contentType === "codeChangesOverview" &&
      chatCodeChangesOverviewItemSchema.parse(
        JSON.parse(codeChangesOverviewMessage.text),
      )) || { versionId: undefined };

  const pageGenerationVersionId =
    (pageGenerationToolResultMessage &&
      pageGenerationToolResultMessage.type === "tool-result" &&
      pageGenerationToolResultMessage.toolName === "generatePageTool" &&
      pageGenerationToolResultMessage.output.value.fileVersionId) ||
    undefined;

  // Find integration config tool call and its result
  const integrationConfigToolCallContent = messages
    .flatMap((msg) => msg.content)
    .find((item) => {
      return (
        item.type === "tool-call" &&
        item.toolName === "getIntegrationConfigTool"
      );
    });

  const integrationConfigToolCall =
    integrationConfigToolCallContent &&
    integrationConfigToolCallContent.type === "tool-call"
      ? (integrationConfigToolCallContent as ToolCallMessageContent)
      : undefined;

  const { toolResult: integrationConfigToolResult } = integrationConfigToolCall
    ? findToolResult(integrationConfigToolCall.toolCallId, messages)
    : { toolResult: null };

  const integrationConfigIsConfigured =
    integrationConfigToolResult?.output?.type === "json" &&
    "success" in integrationConfigToolResult.output.value &&
    integrationConfigToolResult.output.value.success === true;

  return {
    preToolMessages: preToolMessages.filter((msg) => msg.content.length > 0),
    toolSectionMessages: toolSectionMessages.filter(
      (msg) => msg.content.length > 0,
    ),
    postToolMessages: postToolMessages.filter((msg) => msg.content.length > 0),
    suggestTemplatesToolResult: suggestTemplatesToolResult as
      | SuggestTemplatesToolResult
      | undefined,
    suggestTemplatesToolResultMessageId,
    pageGenerationToolResult: pageGenerationToolResult as
      | GeneratePageToolResult
      | undefined,
    pageGenerationToolResultMessageId,
    integrationConfigToolCall,
    integrationConfigIsConfigured,
    fileVersionId: codeChangesOverviewVersionId ?? pageGenerationVersionId,
  };
};

interface MessageGroupProps {
  role: "user" | "assistant";
  messages: ChatMessageType[];
  isChatLoading?: boolean;
  isLastAssistantGroup?: boolean;
}

export default function MessageGroup({
  role,
  messages,
  isChatLoading,
  isLastAssistantGroup,
}: MessageGroupProps) {
  const hasAnyVisibleContent = messages.some((message) => {
    return message.content.some((item) => {
      if (item.type === "text" || item.type === "image") {
        return !item.isHidden;
      }
      return true;
    });
  });

  const chatId = useChatId();
  const chatTurnId = messages[0]?.chatTurnId;

  if (!hasAnyVisibleContent) {
    return null;
  }

  const {
    preToolMessages,
    toolSectionMessages,
    postToolMessages,
    suggestTemplatesToolResult,
    suggestTemplatesToolResultMessageId,
    pageGenerationToolResult,
    pageGenerationToolResultMessageId,
    integrationConfigToolCall,
    integrationConfigIsConfigured,
    fileVersionId,
  } =
    role === "assistant"
      ? splitMessageGroup(messages)
      : {
          preToolMessages: messages,
          toolSectionMessages: [],
          postToolMessages: [],
          suggestTemplatesToolResult: null,
          suggestTemplatesToolResultMessageId: undefined,
          pageGenerationToolResult: null,
          pageGenerationToolResultMessageId: undefined,
          integrationConfigToolCall: undefined,
          integrationConfigIsConfigured: undefined,
          fileVersionId: undefined,
        };

  const headerText = getHeaderText(messages);

  const toolSectionMessagesWithoutPostToolMessages = toolSectionMessages.filter(
    (message) => {
      return !postToolMessages.some(
        (postToolMessage) => postToolMessage.id === message.id,
      );
    },
  );

  const isActivelyStreaming = Boolean(isChatLoading && isLastAssistantGroup);

  return (
    <div className="flex flex-row items-start w-full gap-3">
      {role === "assistant" && (
        <LogoBadge className="h-6 w-6 shrink-0" aria-hidden />
      )}
      <div
        className={twMerge(
          "flex flex-col w-full min-w-0 gap-2",
          role === "user" && "items-end ml-9",
        )}
      >
        {preToolMessages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {toolSectionMessages.length > 0 && (
          <ToolSectionCard
            messages={
              isActivelyStreaming
                ? toolSectionMessages
                : toolSectionMessagesWithoutPostToolMessages
            }
            isStreaming={isActivelyStreaming}
            fileVersionId={fileVersionId}
            headerText={headerText}
          />
        )}

        {/* NOTE (Ben, 2025-11-07): Show suggest templates message separately if it exists since it has special UI */}
        {suggestTemplatesToolResult && (
          <SuggestedTemplates
            toolResult={suggestTemplatesToolResult}
            toolResultMessageId={suggestTemplatesToolResultMessageId}
          />
        )}

        {/* NOTE (Ben, 2025-11-07): Show page generation message if it exists since it has special UI */}
        {pageGenerationToolResult && (
          <PageGenerationBlock
            toolResult={pageGenerationToolResult}
            toolResultMessageId={pageGenerationToolResultMessageId}
          />
        )}

        {/* NOTE (Jasmine, 2026-02-12): Show integration connect card when the AI calls getIntegrationConfigTool */}
        {integrationConfigToolCall && (
          <IntegrationConnectCard
            toolCall={integrationConfigToolCall}
            isConfigured={Boolean(integrationConfigIsConfigured)}
          />
        )}

        {!isActivelyStreaming && postToolMessages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            {postToolMessages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
          </motion.div>
        )}

        <ChatActions
          messages={messages}
          role={role}
          isChatLoading={isChatLoading}
          isLastAssistantGroup={isLastAssistantGroup}
        />

        {role === "assistant" && shouldShowV2Debuggers() && (
          <ChatDebugPanel
            chatId={chatId}
            chatTurnId={chatTurnId}
            messages={messages}
            isActivelyStreaming={isActivelyStreaming}
          />
        )}
      </div>
    </div>
  );
}
